import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table';
import { Button, Table } from 'antd';
import cx from 'classnames';

import { getSortOrderRequestValue } from 'utils/sortUtils';
import { defaultSortParams } from '../../ProfitReportDetailsPage';
import { PreviewWeeklyBillingAndProfitReportViewModel } from 'api/backofficeApiClient';
import { getPreviewTrendWeeklyBillingAndProfitReport } from 'redux/reducers/homeDashboardSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from '../../UserHomePage.module.less';

const columns: ColumnsType<PreviewWeeklyBillingAndProfitReportViewModel> = [
  {
    title: 'Customer Name',
    key: 'customerName',
    render: ({
      customerName,
    }: PreviewWeeklyBillingAndProfitReportViewModel) => (
      <span data-id="customer-name">{customerName || ''}</span>
    ),
  },
  {
    title: 'Worker Type / Worker ID',
    key: 'workerTypeId',
    render: ({
      workerType,
      workerId,
    }: PreviewWeeklyBillingAndProfitReportViewModel) => (
      <>
        {workerType && <div data-id="worker-type">{workerType}</div>}
        {workerId && <div data-id="worker-id">{workerId}</div>}
      </>
    ),
  },
  {
    title: 'Bill Hours (Reg)',
    key: 'billHoursReg',
    render: ({
      billHoursReg,
    }: PreviewWeeklyBillingAndProfitReportViewModel) => (
      <span data-id="bill-hours-reg">{billHoursReg || ''}</span>
    ),
  },
  {
    title: 'Total Profit',
    key: 'totalProfit',
    render: ({ totalProfit }: PreviewWeeklyBillingAndProfitReportViewModel) => (
      <span data-id="total-profit">{totalProfit || ''}</span>
    ),
  },
  {
    title: 'Invoice Number',
    key: 'invoiceNumber',
    render: ({
      invoiceNumber,
    }: PreviewWeeklyBillingAndProfitReportViewModel) => (
      <span data-id="invoice-number">{invoiceNumber || ''}</span>
    ),
  },
];

export default function ProfitReportPreview() {
  const [profitReportPreviewData, setProfitReportPreviewData] =
    useState<PreviewWeeklyBillingAndProfitReportViewModel[]>();
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const loadProfitReportPreviewData = useCallback(async () => {
    const result = await dispatch(
      getPreviewTrendWeeklyBillingAndProfitReport({
        sortField: defaultSortParams.sortBy,
        sortType: getSortOrderRequestValue(defaultSortParams.sortOrder),
        pageNumber: 0,
        pageSize: 5,
      }),
    );

    if (getPreviewTrendWeeklyBillingAndProfitReport.fulfilled.match(result)) {
      setProfitReportPreviewData(result.payload);
    } else {
      setProfitReportPreviewData(undefined);
    }
  }, [dispatch]);

  const loadInitialData = useCallback(async () => {
    await loadProfitReportPreviewData();

    setIsInitialDataLoaded(true);
  }, [loadProfitReportPreviewData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.title}>
        <Link to="/profit-report-details">Profit Report</Link>

        <Button
          type="primary"
          onClick={() => navigate('/profit-report-details')}
        >
          View Details
        </Button>
      </div>

      <Table
        className={cx(styles.table, 'custom-table')}
        columns={columns}
        pagination={false}
        loading={!isInitialDataLoaded}
        dataSource={profitReportPreviewData}
        rowKey={(record) => record?.invoiceNumber as string}
        locale={{
          emptyText: 'You currently do not have any data in the report.',
        }}
      />
    </div>
  );
}
